import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";

const inputCls =
  "w-full h-12 rounded-md border border-border px-4 text-sm outline-none focus:ring-2 focus:ring-gold";

export function MobileOtpForm({ withName = false, onSuccess }) {
  const { requestOtp, verifyOtp } = useAuth();
  const [step, setStep] = useState("mobile");
  const [mobile, setMobile] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState("professional");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [demoOtp, setDemoOtp] = useState("");
  const [agreed, setAgreed] = useState(false);

  const sendOtp = async (e) => {
    e.preventDefault();
    if (withName && !agreed) return;
    setLoading(true);
    try {
      const res = await requestOtp(mobile);
      setStep("otp");
      if (res.demo_otp) {
        setDemoOtp(res.demo_otp);
        toast.success(`Demo OTP: ${res.demo_otp}`);
      } else {
        toast.success("OTP sent to your mobile number.");
      }
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await verifyOtp(mobile, otp, withName ? name : undefined, withName ? category : undefined, withName ? agreed : undefined);
      toast.success(res.is_new ? "Account created. Welcome to KRV Prime." : "Welcome back.");
      onSuccess(res.user);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  if (step === "mobile") {
    return (
      <form onSubmit={sendOtp} className="mt-8 space-y-4" data-testid="mobile-otp-request-form">
        {withName && (
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="otp-name-input"
            placeholder="Full name"
            className={inputCls}
          />
        )}
        {withName && (
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="otp-category"
            className={inputCls}
          >
            <option value="professional">Working Professional</option>
            <option value="student">Student</option>
            <option value="trader">Trader / Investor</option>
          </select>
        )}
        <div className="flex items-center rounded-md border border-border focus-within:ring-2 focus-within:ring-gold">
          <span className="px-4 text-sm text-slate-500 border-r border-border h-12 flex items-center">+91</span>
          <input
            required
            type="tel"
            inputMode="numeric"
            maxLength={10}
            value={mobile}
            onChange={(e) => setMobile(e.target.value.replace(/\D/g, ""))}
            data-testid="otp-mobile-input"
            placeholder="10-digit mobile number"
            className="flex-1 h-12 px-4 text-sm outline-none bg-transparent rounded-r-md"
          />
        </div>
        {withName && (
          <label className="flex items-start gap-2 text-xs text-slate-500 cursor-pointer">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="otp-agree" className="mt-0.5 accent-navy" />
            <span>I have read and agree to the <Link to="/terms" className="text-navy hover:text-gold underline">Terms & Conditions</Link> and <Link to="/privacy" className="text-navy hover:text-gold underline">Privacy Policy</Link>.</span>
          </label>
        )}
        <button
          type="submit"
          disabled={loading || mobile.length !== 10 || (withName && !agreed)}
          data-testid="otp-request-submit"
          className="w-full h-12 rounded-md bg-navy text-white font-semibold text-sm hover:bg-navy-light transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? "Sending OTP…" : "Send OTP"}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={verify} className="mt-8 space-y-4" data-testid="mobile-otp-verify-form">
      <button
        type="button"
        onClick={() => { setStep("mobile"); setOtp(""); setDemoOtp(""); }}
        data-testid="otp-change-mobile"
        className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-navy transition-colors"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Change number (+91 {mobile})
      </button>
      {demoOtp && (
        <div
          data-testid="otp-demo-hint"
          className="rounded-md bg-gold/10 border border-gold/30 px-4 py-2.5 text-xs text-navy"
        >
          Demo mode — your OTP is <span className="font-bold tracking-widest">{demoOtp}</span>
        </div>
      )}
      <input
        required
        type="text"
        inputMode="numeric"
        maxLength={6}
        value={otp}
        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
        data-testid="otp-code-input"
        placeholder="Enter 6-digit OTP"
        className={inputCls + " tracking-[0.4em] text-center font-semibold"}
      />
      <button
        type="submit"
        disabled={loading || otp.length !== 6}
        data-testid="otp-verify-submit"
        className="w-full h-12 rounded-md bg-navy text-white font-semibold text-sm hover:bg-navy-light transition-colors disabled:opacity-60"
      >
        {loading ? "Verifying…" : "Verify & Continue"}
      </button>
      <button
        type="button"
        disabled={loading}
        onClick={sendOtp}
        data-testid="otp-resend"
        className="w-full text-xs text-slate-500 hover:text-gold transition-colors"
      >
        Didn't get it? Resend OTP
      </button>
    </form>
  );
}
